import { ChangeDetectionStrategy, Component, computed, Input, inject, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { LanguageDetectionService } from '@services/domain/language-detection.service';
import { UserService } from '@services/user/user.service';

@Component({
	selector: 'app-lang-switcher',
	imports: [TranslocoModule],
	templateUrl: './lang-switcher.component.html',
	styleUrl: './lang-switcher.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LangSwitcherComponent {
	@Input() isFloating = false; // Floating or inline mode

	private transloco = inject(TranslocoService);
	private languageDetectionService = inject(LanguageDetectionService);
	private currentUserService = inject(CurrentUserService);
	private userService = inject(UserService);

	// Signal for active language
	activeLang = signal(this.transloco.getActiveLang());

	// Signal for floating menu state
	isFloatingMenuOpen = signal(false);

	// Computed to check if language is active
	isActive = computed(() => (lang: string) => this.activeLang() === lang);

	constructor() {
		// Listen to Transloco language changes
		this.transloco.langChanges$.subscribe((lang) => {
			this.activeLang.set(lang);
		});
	}

	setLang(lang: string) {
		this.transloco.setActiveLang(lang);
		this.activeLang.set(lang);
		this.persistLanguagePreference(lang);
	}

	private persistLanguagePreference(lang: string): void {
		// Always update localStorage immediately
		this.currentUserService.updatePreferredLanguage(lang);

		// Also persist to backend if user is logged in
		const userId = this.currentUserService.getUserId();
		if (userId) {
			this.userService.updateUser(String(userId), { preferredLanguage: lang }).subscribe({
				error: (err) => {
					console.error('Error saving language preference:', err);
				},
			});
		}
	}

	// Methods for floating mode
	toggleFloatingMenu() {
		this.isFloatingMenuOpen.set(!this.isFloatingMenuOpen());
	}

	selectLanguage(lang: string) {
		this.setLang(lang);
		this.isFloatingMenuOpen.set(false); // Close menu after selecting
	}

	getCurrentFlag(): string {
		const lang = this.activeLang();
		if (lang === 'es') return '🇪🇸';
		if (lang === 'ca') return '🇪🇸';
		if (lang === 'fr') return '🇫🇷';
		if (lang === 'zh') return '🇨🇳';
		return '🇬🇧';
	}

	// Detect system language
	async detectSystemLanguage() {
		await this.languageDetectionService.detectAndSetLanguage();
	}

	// Get system language (for display only)
	async getSystemLanguage(): Promise<string> {
		return await this.languageDetectionService.getCurrentSystemLanguage();
	}
}
